import { Link as MaterialLink } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CancellationsApi from '../../../api/CancellationsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { CANCELLATION_COMMENT_MAX_LENGTH } from '../../../data/constants';
import { HoldbackResponse, PolicyCharacteristics } from '../../../types/policy';
import {
	formatCurrency,
	getActiveCharacteristics,
	getDateErrors,
} from '../../../util/util';
import { StyledDetail, StyledSectionContainer } from '../../common';
import FieldLabel from '../../fields/FieldLabel';
import DefaultDatePicker from '../../form/DefaultDatePicker';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';
import { getCategoryOptions, getTypeOptionsByCategory } from './util';

interface Props {
	authData: AuthData;
	cancellationData: CancellationData;
}

const useStyles = makeStyles(() => ({
	linkWrapper: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
}));

const CancellationDetails = (props: Props) => {
	const classes = useStyles();
	const { cancellationData, authData } = props;
	const {
		cancellationUpdateObj,
		selectedCancellation,
		setCancellationUpdateObj,
		setSelectedCancellation,
		setLoading,
		policy,
		productConfig,
	} = cancellationData;

	const {
		cancellationCategory,
		title,
		cancellationComments: comments,
		name: cancellationName,
	} = selectedCancellation;
	const currency = policy?.currency;
	const totalChange = selectedCancellation.price
		? parseFloat(selectedCancellation.price.totalChange)
		: 0;
	const { effectiveContractEndTimestamp } = policy;
	const { policyStartTimestamp } = getActiveCharacteristics(
		policy.characteristics,
	)[0] as PolicyCharacteristics;

	const effectiveTimestamp =
		selectedCancellation.effectiveTimestamp ||
		cancellationUpdateObj.effectiveTimestamp;
	const effectiveDateValue = effectiveTimestamp
		? new Date(parseInt(effectiveTimestamp, 10))
		: null;

	const [effectiveDate, setEffectiveDate] = useState(effectiveDateValue);
	const [selectedCategory, setSelectedCategory] = useState({
		value: cancellationCategory,
		label: cancellationCategory,
	});
	const [selectedType, setSelectedType] = useState({
		value: cancellationName,
		label: title,
	});
	const [cancellationComments, setCancellationComments] = useState(
		comments || '',
	);
	const maxDate =
		selectedCancellation.state === 'issued'
			? null
			: new Date(parseInt(effectiveContractEndTimestamp, 10));
	if (selectedCancellation.state !== 'issued') {
		maxDate?.setDate(maxDate?.getDate() - 1);
	}
	const minDate =
		selectedCancellation.state === 'issued'
			? null
			: new Date(parseInt(policyStartTimestamp, 10));
	const effectiveDateErrors = getDateErrors(
		effectiveDate,
		minDate,
		maxDate,
		true,
		authData,
	);
	const readOnlyMode =
		!authData.hasPermission('cancellation.update') ||
		selectedCancellation.state !== 'draft';

	useEffect(() => {
		setEffectiveDate(effectiveDateValue);
	}, [selectedCancellation, cancellationUpdateObj]);

	const setNewEffectiveTimestamp = async (date: Date) => {
		const validDate = date && !isNaN(date.getTime());
		setEffectiveDate(date);
		cancellationUpdateObj.effectiveTimestamp = validDate
			? `${date.getTime()}`
			: null;
		setCancellationUpdateObj(cancellationUpdateObj);
		if (validDate) {
			try {
				setLoading(true);
				const updatedCancellation = await CancellationsApi.updateCancellation(
					authData.auth.authorizationToken,
					selectedCancellation.locator,
					cancellationUpdateObj,
				);
				setSelectedCancellation(updatedCancellation.data);
			} catch (error) {
				console.error(error);
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			} finally {
				setLoading(false);
			}
		}
	};

	const renderReturnAmount = () => {
		if (selectedCancellation.state === 'rescinded') {
			return null;
		}

		return (
			<FormControl style={{ flexGrow: 1, marginRight: '20px' }}>
				<FieldLabel title='Return Amount' id='returnAmountLabel' />
				<TextField
					id='returnAmount'
					value={formatCurrency(totalChange, currency)}
					disabled
				/>
			</FormControl>
		);
	};

	const renderHoldbackAmount = () => {
		if (selectedCancellation.state === 'rescinded') {
			return null;
		}

		const filteredHoldbacks = selectedCancellation.price
			? selectedCancellation.price.holdbacks.filter(
					(h) => h.type !== 'commission',
			  )
			: [];
		const holdbackChange = filteredHoldbacks
			? filteredHoldbacks.reduce(
					(a: number, b: HoldbackResponse) =>
						b.isReversed ? a - parseFloat(b.amount) : a + parseFloat(b.amount),
					0,
			  )
			: 0;
		return (
			<FormControl style={{ flexGrow: 1 }}>
				<FieldLabel title='Holdback Amount' id='holdbackAmountLabel' />
				<TextField
					id='holdbackAmount'
					value={formatCurrency(holdbackChange, currency)}
					disabled
				/>
			</FormControl>
		);
	};

	const renderEffectiveDate = () => {
		return (
			<StyledDetail style={{ width: '25%', flexGrow: 1 }}>
				<DefaultDatePicker
					value={effectiveDate}
					minDate={minDate}
					maxDate={maxDate}
					error={!readOnlyMode && !effectiveDateErrors.isValid}
					errorMessage={
						!readOnlyMode && !effectiveDateErrors.isValid
							? effectiveDateErrors.message
							: null
					}
					required={!readOnlyMode}
					onChange={setNewEffectiveTimestamp}
					label={'Cancellation Effective Date'}
					showCalendar
					disabled={readOnlyMode}
					style={{ marginRight: '10px' }}
					id={'effective-date'}
				/>
			</StyledDetail>
		);
	};

	const { categoryOptions, categoryTypeOptions } = getCategoryOptions(
		selectedCategory.value,
		productConfig,
	);
	const isCategoryEnabled = !_.isEmpty(categoryOptions);
	const onTypeChange = (opt: SelectValue) => {
		setSelectedType(opt);
		cancellationUpdateObj.name = opt.value;
		setCancellationUpdateObj(cancellationUpdateObj);
	};

	const renderCancellationCategory = () => {
		if (!isCategoryEnabled) {
			return null;
		}

		return (
			<StyledDetail style={{ width: '50%' }}>
				<FieldLabel title='Cancellation Category' required />
				<ReactSelect
					id='cancellation-category'
					options={categoryOptions}
					value={selectedCategory}
					isDisabled={readOnlyMode}
					onChange={(opt: SelectValue) => {
						setSelectedCategory(opt);
						cancellationUpdateObj.cancellationCategory = opt.value;
						const newTypeOptions = getTypeOptionsByCategory(
							opt.value,
							productConfig,
						);

						if (!newTypeOptions.find((co) => co.value === selectedType.value)) {
							onTypeChange({ value: '', label: '' }); // clear selected type if not included newly selected category
						}
						setCancellationUpdateObj(cancellationUpdateObj);
					}}
				/>
			</StyledDetail>
		);
	};

	const renderCancellationType = () => {
		return (
			<StyledDetail style={{ width: '50%' }}>
				<FieldLabel title='Cancellation Name' required />
				<ReactSelect
					id='cancellation-type'
					options={categoryTypeOptions}
					value={selectedType}
					isDisabled={readOnlyMode || !isCategoryEnabled}
					onChange={onTypeChange}
				/>
			</StyledDetail>
		);
	};

	const renderCancellationComments = () => {
		if (!isCategoryEnabled) {
			return null;
		}

		const hasError =
			!!cancellationComments &&
			cancellationComments.length === CANCELLATION_COMMENT_MAX_LENGTH;
		const commentErrorMessage = hasError
			? `Can't be more than ${CANCELLATION_COMMENT_MAX_LENGTH} characters`
			: '';
		return (
			<StyledDetail style={{ width: '50%' }}>
				<FieldLabel title='Cancellation Comments' />
				<TextField
					id='cancellation-comments'
					value={cancellationComments}
					disabled={readOnlyMode}
					error={hasError}
					helperText={commentErrorMessage}
					multiline
					rows={5}
					onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
						const commentValue = event.currentTarget.value;

						if (commentValue?.length <= CANCELLATION_COMMENT_MAX_LENGTH) {
							setCancellationComments(commentValue);
							cancellationUpdateObj.cancellationComments = commentValue;
							setCancellationUpdateObj(cancellationUpdateObj);
						}
					}}
					variant='outlined'
				/>
			</StyledDetail>
		);
	};

	return (
		<>
			<div className={classes.linkWrapper}>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink component={RouterLink} to='../auxData'>
						Aux Data
					</MaterialLink>
				)}
			</div>
			<StyledSectionContainer style={{ display: 'flex' }}>
				{renderEffectiveDate()}
				{renderReturnAmount()}
				{renderHoldbackAmount()}
			</StyledSectionContainer>
			<StyledSectionContainer style={{ display: 'flex' }}>
				{renderCancellationCategory()}
				{renderCancellationType()}
			</StyledSectionContainer>
			<StyledSectionContainer style={{ display: 'flex' }}>
				{renderCancellationComments()}
			</StyledSectionContainer>
		</>
	);
};

export default withCancellationContext<Props>(CancellationDetails);
